import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard, FileText, ShoppingCart, MessageSquare, Settings,
  Upload, Users, Truck, Receipt, FileCode, Package, X, Menu, Database, Zap,
  Calculator, TrendingDown, TrendingUp, Power
} from 'lucide-react';
import { IS_TAURI } from '@/core/environment';
import { extendMenu } from '@/core/vertical-engine';
import { Calendar, Inbox as InboxIcon, Layout, HardHat, Clock } from 'lucide-react';
import { APP_ICON_PATH, APP_NAME } from '@/config/branding';

const iconMap: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  Calendar, Inbox: InboxIcon, Layout, HardHat, Calculator, Clock,
  TrendingDown, TrendingUp, Users,
};

const Sidebar = ({ isOpen, toggleSidebar }: { isOpen: boolean; toggleSidebar: () => void }) => {
  const coreNavItems = [
    { path: '/', icon: Power, label: 'Ignition' },
    { path: '/contacts', icon: Users, label: 'Clientes' },
    { path: '/suppliers', icon: Truck, label: 'Proveedores' },
    { path: '/invoices', icon: FileText, label: 'Facturas' },
    { path: '/quotes', icon: ShoppingCart, label: 'Presupuestos' },
    { path: '/expenses', icon: Zap, label: 'Gastos' },
    { path: '/items', icon: Package, label: 'Catálogo' },
    { path: '/templates', icon: FileCode, label: 'Plantillas' },
    { path: '/inbox', icon: MessageSquare, label: 'Comunicaciones' },
    { path: '/import', icon: Upload, label: 'Importar' },
    { path: '/settings', icon: Settings, label: 'Configuración' },
  ];

  const verticalItems = extendMenu();

  return (
    <>
      <button
        className="md:hidden fixed top-4 left-4 z-50 p-2 cut-corner bg-[var(--bg-card)] border-2 border-[var(--border-color)] text-[var(--accent)] shadow-[0_0_15px_#000]"
        onClick={toggleSidebar}
      >
        <Menu size={24} />
      </button>

      <aside
        className={`fixed top-0 left-0 z-40 h-screen bg-[#060608] border-r-2 border-[var(--accent)]/30 transition-transform ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } md:translate-x-0 w-64 flex flex-col shadow-[4px_0_25px_var(--accent-soft)]`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-6 bg-[#000] border-b-2 border-[#111] caution-stripe relative overflow-hidden">
          <div className="absolute inset-0 bg-black/60 z-0"></div>
          <div className="flex items-center gap-4 min-w-0 relative z-10">
            <div className="w-12 h-12 cut-corner border-[3px] border-[var(--accent)] bg-[#030303] p-1.5 flex-shrink-0 shadow-[0_0_15px_var(--accent-glow)]">
              <img src={APP_ICON_PATH} alt="" className="w-full h-full object-contain" />
            </div>
            <div className="min-w-0">
              <span className="text-3xl font-bold font-display text-[var(--text-primary)] whitespace-nowrap block truncate tracking-widest drop-shadow-[0_0_8px_var(--text-secondary)]">
                {APP_NAME}
              </span>
            {IS_TAURI && (
              <div className="flex items-center gap-1.5 mt-1 font-mono tracking-widest uppercase">
                <Database size={10} className="text-emerald-500 animate-pulse" />
                <span className="text-[10px] text-emerald-500 font-bold border border-emerald-500/30 px-1 py-0.5 rounded-[2px] bg-emerald-500/10">LOCAL DB</span>
              </div>
            )}
            </div>
          </div>
          <button
            className="md:hidden p-1 text-[var(--text-muted)] hover:text-white relative z-10 transition-colors"
            onClick={toggleSidebar}
          >
            <X size={24} />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-4 py-6 overflow-y-auto bg-mesh relative">
          <ul className="space-y-1.5 relative z-10">
            {coreNavItems.map((item) => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  end={item.path === '/'}
                  className={({ isActive }) =>
                    `flex items-center gap-4 px-4 py-3 text-lg font-display uppercase tracking-widest transition-all duration-300 cut-corner border border-transparent ${
                      isActive
                        ? 'bg-[var(--accent)] text-[#000] border-[#000] shadow-[0_0_20px_var(--accent-glow)] font-bold'
                        : 'text-[var(--text-secondary)] hover:bg-[#111] hover:text-[var(--text-primary)] hover:border-[var(--border-color)]'
                    }`
                  }
                  onClick={() => window.innerWidth < 768 && toggleSidebar()}
                >
                  <item.icon size={20} className="flex-shrink-0 opacity-90" />
                  <span>{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>

          {verticalItems.length > 0 && (
            <>
              <div className="mt-8 mb-4 px-2 flex items-center gap-3">
                <div className="flex-1 h-0.5 bg-[var(--accent)]/30" />
                <span className="text-sm font-display font-bold text-[var(--accent)] uppercase tracking-[0.2em] drop-shadow-[0_0_5px_var(--accent-glow)]">
                  // MODS
                </span>
                <div className="flex-1 h-0.5 bg-[var(--accent)]/30" />
              </div>
              <ul className="space-y-1.5 relative z-10">
                {verticalItems.map((item) => {
                  const iconStr = typeof item.icon === 'string' ? item.icon : '';
                  const IconComponent = iconStr ? iconMap[iconStr] : undefined;
                  const to = (item as any).to ?? item.path;
                  return (
                    <li key={to}>
                      <NavLink
                        to={to}
                        className={({ isActive }) =>
                          `flex items-center gap-4 px-4 py-3 text-lg font-display uppercase tracking-widest transition-all duration-300 cut-corner border border-transparent ${
                            isActive
                              ? 'bg-[var(--accent)] text-[#000] border-[#000] shadow-[0_0_20px_var(--accent-glow)] font-bold'
                              : 'text-[var(--text-secondary)] hover:bg-[#111] hover:text-[var(--text-primary)] hover:border-[var(--border-color)]'
                          }`
                        }
                        onClick={() => window.innerWidth < 768 && toggleSidebar()}
                      >
                        {IconComponent && <IconComponent size={20} className="flex-shrink-0 opacity-90" />}
                        <span>{item.label}</span>
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </nav>

        {/* Footer */}
        <div className="border-t-2 border-[#111] px-6 py-4 bg-[#030303]">
          <p className="text-[10px] font-mono tracking-widest text-[var(--text-muted)] uppercase">
            SYSVER: {typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '2.0.0'}
            <br/><span className="text-[var(--accent)]">{IS_TAURI ? '[DESKTOP BUILD]' : '[WEB TERMINAL]'}</span>
          </p>
        </div>
      </aside>

      {isOpen && (
        <div
          className="fixed inset-0 z-30 bg-[#000]/80 backdrop-blur-md md:hidden"
          onClick={toggleSidebar}
        />
      )}
    </>
  );
};

export default Sidebar;
